import type { FormEvent, JSX } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import { Button, Input } from '@artosera/ui';
import { creerTypeRecette, modifierTypeRecette, supprimerTypeRecette } from '../../lib/api.js';
import { analyserMontant, montantEnSaisie } from '../../lib/montant.js';
import type { TypeRecetteVue } from '../../types.js';
import { useMessageErreur } from '../Finances/commun.js';
import commun from '../VueDAigle/commun.module.css';
import { useFormats } from '../VueDAigle/formats.js';
import styles from './Comptabilite.module.css';
import { useObjectif } from './Encaissements.js';
import { useRelireComptabilite } from './useComptabilite.js';

interface Saisie {
  fr: string;
  en: string;
  objectif: string;
}

/** Le corps d'un type : son libellé (l'anglais s'il est donné), son objectif en centimes. */
function corpsDe(
  saisie: Saisie,
): { libelle: { fr: string; en?: string }; objectifHtCentimes: number | null } | null {
  const objectif = analyserMontant(saisie.objectif);
  if (!objectif.ok || saisie.fr.trim() === '') return null;
  const en = saisie.en.trim();
  return {
    libelle: en === '' ? { fr: saisie.fr.trim() } : { fr: saisie.fr.trim(), en },
    objectifHtCentimes: objectif.valeur === 0 ? null : objectif.valeur,
  };
}

/** Les trois champs d'un type : nom, nom anglais, objectif HT. */
function ChampsType({
  prefixe,
  saisie,
  surChange,
}: {
  prefixe: string;
  saisie: Saisie;
  surChange: (saisie: Saisie) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const objectifInvalide = !analyserMontant(saisie.objectif).ok;
  return (
    <div className={styles.encaissementChamps}>
      <div>
        <label className={styles.etiquetteChamp} htmlFor={`${prefixe}-fr`}>
          {t('comptabilite.types.libelleFr')}
        </label>
        <Input
          id={`${prefixe}-fr`}
          autoComplete="off"
          maxLength={80}
          required
          value={saisie.fr}
          onChange={(e) => surChange({ ...saisie, fr: e.target.value })}
        />
      </div>
      <div>
        <label className={styles.etiquetteChamp} htmlFor={`${prefixe}-en`}>
          {t('comptabilite.types.libelleEn')}
        </label>
        <Input
          id={`${prefixe}-en`}
          autoComplete="off"
          maxLength={80}
          lang="en"
          value={saisie.en}
          onChange={(e) => surChange({ ...saisie, en: e.target.value })}
        />
      </div>
      <div>
        <label className={styles.etiquetteChamp} htmlFor={`${prefixe}-objectif`}>
          {t('comptabilite.types.objectif')}
        </label>
        <span className={styles.avecSuffixe}>
          <Input
            id={`${prefixe}-objectif`}
            className={styles.champMontant}
            inputMode="decimal"
            autoComplete="off"
            mono
            aria-invalid={objectifInvalide || undefined}
            aria-describedby={`${prefixe}-objectif-aide`}
            value={saisie.objectif}
            onChange={(e) => surChange({ ...saisie, objectif: e.target.value })}
          />
          <span className={styles.suffixe} aria-hidden="true">
            €
          </span>
        </span>
        <p id={`${prefixe}-objectif-aide`} className={styles.aideChamp}>
          {objectifInvalide
            ? t('comptabilite.encaissements.montantInvalide')
            : t('comptabilite.types.objectifAide')}
        </p>
      </div>
    </div>
  );
}

/**
 * Les types de recette créés par la direction (dans la section des
 * pourcentages) : créer, renommer, fixer un objectif, archiver ou rétablir, et
 * supprimer celui qui n'a jamais servi. La grille d'un type se règle dans sa
 * ligne, au-dessus, comme celle d'un service. Les cinq services de base ne
 * figurent pas ici : ils ne se touchent pas.
 */
export function TypesRecette({ types }: { types: readonly TypeRecetteVue[] }): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const elevation = useElevation();
  const phraseObjectif = useObjectif();
  const message = useMessageErreur('comptabilite');
  const relire = useRelireComptabilite();
  const id = useId();
  const vide: Saisie = { fr: '', en: '', objectif: '' };
  const [nouveau, setNouveau] = useState<Saisie>(vide);
  const [enEdition, setEnEdition] = useState<{ id: string; saisie: Saisie } | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [fait, setFait] = useState<string | null>(null);
  const crees = types.filter((type) => !type.base);

  const executer = async (cle: string, geste: () => Promise<unknown>): Promise<boolean> => {
    setEnCours(cle);
    setErreur(null);
    setFait(null);
    try {
      await geste();
      return true;
    } catch (echec) {
      if (!estElevationAnnulee(echec)) setErreur(message(echec));
      return false;
    } finally {
      setEnCours(null);
      await relire();
    }
  };

  const creer = async (evenement: FormEvent): Promise<void> => {
    evenement.preventDefault();
    const corps = corpsDe(nouveau);
    if (corps === null) return;
    if (await executer('creation', () => creerTypeRecette(corps))) {
      setFait(t('comptabilite.types.cree', { nom: corps.libelle.fr }));
      setNouveau(vide);
    }
  };

  return (
    <div className={styles.exclusions}>
      <h3 className={styles.exclusionsTitre}>{t('comptabilite.types.titre')}</h3>
      <p className={commun.note}>{t('comptabilite.types.note')}</p>
      {crees.length === 0 ? (
        <p className={commun.note}>{t('comptabilite.types.aucun')}</p>
      ) : (
        <ul className={styles.encaissementsListe}>
          {crees.map((type) => {
            const nom = type.libelle?.fr ?? type.id;
            const edition = enEdition?.id === type.id ? enEdition : null;
            return (
              <li key={type.id} className={styles.encaissement}>
                <div className={styles.encaissementTete}>
                  <span className={styles.serviceNom}>
                    {nom}
                    {type.libelle?.en ? (
                      <span className={commun.celluleMuette} lang="en">
                        {' '}
                        · {type.libelle.en}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={`${commun.pastille} ${type.actif ? commun.pastilleVert : commun.pastilleAmbre}`}
                  >
                    {type.actif ? t('comptabilite.types.actif') : t('comptabilite.types.archive')}
                  </span>
                </div>
                <p className={commun.note}>
                  {phraseObjectif(type) ??
                    t('comptabilite.types.sansObjectif', {
                      recu: f.euros(type.encaisseHtCentimes ?? 0),
                    })}
                </p>
                {type.actif ? null : (
                  <p className={commun.noteDiscrete}>{t('comptabilite.types.aideArchive')}</p>
                )}
                {edition ? (
                  <form
                    onSubmit={(evenement) => {
                      evenement.preventDefault();
                      const corps = corpsDe(edition.saisie);
                      if (corps === null) return;
                      void executer(type.id, () => modifierTypeRecette(type.id, corps)).then(
                        (ok) => {
                          if (ok) setEnEdition(null);
                        },
                      );
                    }}
                  >
                    <ChampsType
                      prefixe={`${id}-${type.id}`}
                      saisie={edition.saisie}
                      surChange={(saisie) => setEnEdition({ id: type.id, saisie })}
                    />
                    <div className={styles.actions}>
                      <Button
                        type="submit"
                        size="sm"
                        loading={enCours === type.id}
                        disabled={enCours !== null || corpsDe(edition.saisie) === null}
                      >
                        {t('comptabilite.types.enregistrer')}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={enCours !== null}
                        onClick={() => setEnEdition(null)}
                      >
                        {t('comptabilite.types.annuler')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className={styles.actions}>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={enCours !== null}
                      aria-label={t('comptabilite.types.modifierAria', { nom })}
                      onClick={() =>
                        setEnEdition({
                          id: type.id,
                          saisie: {
                            fr: type.libelle?.fr ?? '',
                            en: type.libelle?.en ?? '',
                            objectif:
                              type.objectifHtCentimes === null
                                ? ''
                                : montantEnSaisie(type.objectifHtCentimes),
                          },
                        })
                      }
                    >
                      {t('comptabilite.types.modifier')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={enCours === `${type.id}-actif`}
                      disabled={enCours !== null}
                      aria-label={t(
                        type.actif
                          ? 'comptabilite.types.archiverAria'
                          : 'comptabilite.types.retablirAria',
                        { nom },
                      )}
                      onClick={() =>
                        void executer(`${type.id}-actif`, () =>
                          modifierTypeRecette(type.id, { actif: !type.actif }),
                        )
                      }
                    >
                      {type.actif
                        ? t('comptabilite.types.archiver')
                        : t('comptabilite.types.retablir')}
                    </Button>
                    {type.utilise ? null : (
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={enCours === `${type.id}-suppression`}
                        disabled={enCours !== null}
                        aria-label={t('comptabilite.types.supprimerAria', { nom })}
                        onClick={() =>
                          void executer(`${type.id}-suppression`, () =>
                            elevation.executer(() => supprimerTypeRecette(type.id)),
                          )
                        }
                      >
                        {t('comptabilite.types.supprimer')}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <form
        className={styles.typeForm}
        aria-labelledby={`${id}-nouveau-titre`}
        onSubmit={(evenement) => void creer(evenement)}
        noValidate
      >
        <h4 id={`${id}-nouveau-titre`} className={styles.apercuTitre}>
          {t('comptabilite.types.nouveau')}
        </h4>
        <ChampsType prefixe={`${id}-nouveau`} saisie={nouveau} surChange={setNouveau} />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="secondary"
            loading={enCours === 'creation'}
            disabled={enCours !== null || corpsDe(nouveau) === null}
          >
            {t('comptabilite.types.creer')}
          </Button>
        </div>
      </form>
      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
      {fait ? (
        <p className={styles.succes} role="status">
          {fait}
        </p>
      ) : null}
      {elevation.dialogue}
    </div>
  );
}
